import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "@src/app/store";
import type {
  CustomSeedRequest,
  InteractiveSqlExecuteResponse,
  SeedFileDto,
} from "./InteractiveSqlApiSlice";

/**
 * Talks to the same execution engine as InteractiveSqlApiSlice, on the
 * /interactive-mongo/* routes. Seed-file and execute payload shapes are shared with the
 * SQL slice, so those types are re-used rather than duplicated.
 */

/** One collection in the connected Mongo database. */
export interface MongoCollectionDto {
  name: string;
  /** Field names inferred from sampled documents */
  fields?: string[];
  documentCount?: number;
}

/** Response of /interactive-mongo/schema. */
export interface InteractiveSchemaDto {
  databaseName?: string;
  collections: MongoCollectionDto[];
}

const RUN_BASE_URL: string =
  import.meta.env.VITE_TEST_FRESHERS_RUN_BASE_URL ||
  "http://localhost:8092/api";

const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: RUN_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const accessToken = state.authSlice?.accessToken ?? "";
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export const InteractiveMongoApiSlice = createApi({
  reducerPath: "interactiveMongoApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["MongoSchema", "SeedFiles"],
  endpoints: (builder) => ({

    getMongoSeedFiles: builder.query<SeedFileDto[], void>({
      query: () => "/interactive-mongo/seed/files",
      providesTags: ["SeedFiles"],
    }),

    getMongoSeedFile: builder.query<SeedFileDto, string>({
      query: (tableName) => `/interactive-mongo/seed/files/${tableName}`,
      providesTags: (_result, _error, tableName) => [
        { type: "SeedFiles", id: tableName },
      ],
    }),

    saveMongoSeedFile: builder.mutation<
      InteractiveSqlExecuteResponse,
      CustomSeedRequest
    >({
      query: (payload) => ({
        url: "/interactive-mongo/seed/save-file",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["SeedFiles", "MongoSchema"],
    }),

    deleteMongoSeedFile: builder.mutation<{ success: boolean }, string>({
      query: (tableName) => ({
        url: `/interactive-mongo/seed/files/${tableName}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SeedFiles", "MongoSchema"],
    }),

    // A script can create or drop collections, so the schema is invalidated too
    executeInteractiveMongo: builder.mutation<
      InteractiveSqlExecuteResponse,
      { sql: string }
    >({
      query: (payload) => ({
        url: "/interactive-mongo/execute",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["MongoSchema"],
    }),

    resetMongoDatabase: builder.mutation<InteractiveSqlExecuteResponse, void>({
      query: () => ({
        url: "/interactive-mongo/reset",
        method: "POST",
      }),
      invalidatesTags: ["MongoSchema"],
    }),
  }),
});

export const {
  useGetMongoSeedFilesQuery,
  useGetMongoSeedFileQuery,
  useLazyGetMongoSeedFileQuery,
  useSaveMongoSeedFileMutation,
  useDeleteMongoSeedFileMutation,
  useExecuteInteractiveMongoMutation,
  useResetMongoDatabaseMutation,
} = InteractiveMongoApiSlice;
